/* Pincode Checker — storefront behaviour. Talks only to your Worker (data-endpoint),
   which is the only thing that ever sees Eshopbox credentials. */
(function () {
  if (customElements.get('pincode-checker')) return;

  const PIN_RE = /^[1-9][0-9]{5}$/; // Indian PIN: 6 digits, first never 0
  const REQUEST_TIMEOUT_MS = 12000;

  class PincodeChecker extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-pincode-input]');
      this.button = this.querySelector('[data-pincode-check]');
      this.buttonLabel = this.querySelector('[data-button-label]');
      this.spinner = this.querySelector('[data-spinner]');
      this.result = this.querySelector('[data-result]');

      if (!this.input || !this.button || !this.result) return; // markup missing, fail silently

      this.endpoint = this.dataset.endpoint || '';
      this.format = this.dataset.format === 'days' ? 'days' : 'date';
      this.controller = null; // aborts the in-flight request

      this._onSubmit = this.handleSubmit.bind(this);
      this.button.addEventListener('click', this._onSubmit);

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.handleSubmit(); }
      });
      // Sanitise to digits as the user types; clearing the field resets the result.
      this.input.addEventListener('input', () => {
        const cleaned = this.input.value.replace(/\D/g, '').slice(0, 6);
        if (cleaned !== this.input.value) this.input.value = cleaned;
        this.input.setAttribute('aria-invalid', 'false');
        if (cleaned === '') this.clearResult();
      });
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this._onSubmit);
      this.controller?.abort();
    }

    /* ---------- submit flow ---------- */

    async handleSubmit() {
      if (this.loading) return; // debounce double-clicks / repeated Enter
      const pincode = (this.input.value || '').trim();

      if (!PIN_RE.test(pincode)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        this.renderState('invalid', this.msg('invalid'));
        return;
      }
      if (!this.endpoint) {
        this.renderState('error', this.msg('error')); // misconfigured: no endpoint set
        return;
      }

      this.setLoading(true);
      this.controller?.abort();
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deliveryPincode: pincode }),
          signal: this.controller.signal,
        });

        let data = {};
        try { data = await res.json(); } catch { /* tolerate empty/non-JSON bodies */ }

        if (res.status === 400 || data.error === 'INVALID_PINCODE') {
          this.input.setAttribute('aria-invalid', 'true');
          this.renderState('invalid', this.msg('invalid'));
        } else if (res.ok && data.available === true) {
          this.renderAvailable(data);
        } else if (res.ok && data.available === false) {
          this.renderState('failure', this.msg('unavailable'));
        } else {
          this.renderError(); // 429 / 502 / 500 / unexpected shape
        }
      } catch (err) {
        // AbortError = timeout or a newer request superseded this one.
        if (err.name === 'AbortError') this.renderError();
        else this.renderError();
      } finally {
        clearTimeout(timeout);
        this.setLoading(false);
      }
    }

    /* ---------- loading state ---------- */

    setLoading(on) {
      this.loading = on;
      this.button.disabled = on;
      this.button.setAttribute('aria-busy', String(on));
      if (this.buttonLabel) this.buttonLabel.hidden = on;
      if (this.spinner) this.spinner.hidden = !on;
    }

    /* ---------- rendering ---------- */

    renderAvailable(data) {
      const etaText = this.formatEta(data);
      const etaLabel = this.msg('etaLabel');
      this.renderState(
        'success',
        this.msg('available'),
        etaText ? `${etaLabel} <strong>${this.esc(etaText)}</strong>` : ''
      );
    }

    renderError() {
      this.renderState('error', this.msg('error'), '', /* withRetry */ true);
    }

    renderState(state, statusText, etaHtml, withRetry) {
      const icon = state === 'success' ? '\u2713' : '\u2715'; // ✓ / ✕
      const showIcon = state === 'success' || state === 'failure';
      this.result.className = 'pincode-checker__result pincode-checker__result--' + state;
      this.result.hidden = false;
      this.result.innerHTML =
        '<span class="pincode-checker__result-status">' +
          (showIcon ? `<span class="pincode-checker__icon" aria-hidden="true">${icon}</span>` : '') +
          `<span>${this.esc(statusText)}</span>` +
        '</span>' +
        (etaHtml ? `<div class="pincode-checker__eta">${etaHtml}</div>` : '');

      if (withRetry) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pincode-checker__retry';
        btn.textContent = 'Retry';
        btn.addEventListener('click', () => this.handleSubmit());
        this.result.appendChild(btn);
      }
    }

    clearResult() {
      this.result.hidden = true;
      this.result.innerHTML = '';
      this.result.className = 'pincode-checker__result';
    }

    /* ---------- eta formatting ---------- */

    formatEta(data) {
      if (this.format === 'days' && Number.isInteger(data.etdDays)) {
        if (data.etdDays <= 0) return 'Today';
        if (data.etdDays === 1) return 'Tomorrow';
        return `in ${data.etdDays} days`;
      }
      return (data.etd || '').trim(); // authoritative date string from the API
    }

    /* ---------- helpers ---------- */

    msg(key) {
      const map = {
        available: this.dataset.msgAvailable || 'Delivery available',
        etaLabel: this.dataset.msgEtaLabel || 'Estimated Delivery:',
        unavailable: this.dataset.msgUnavailable || 'Delivery not available',
        invalid: this.dataset.msgInvalid || 'Invalid pincode',
        error: this.dataset.msgError || "Couldn't check right now. Please try again.",
      };
      return map[key];
    }

    esc(str) {
      const d = document.createElement('div');
      d.textContent = String(str);
      return d.innerHTML;
    }
  }

  customElements.define('pincode-checker', PincodeChecker);
})();